import {
  StrictMode,
  useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  Outlet,
} from 'react-router-dom';
import * as is from 'is';
import { useEffectOnce } from '@utils/react/hooks';
import {
  isMicroapp,
  generateMicroappMessenger,
  getLogger,
  createNavEntry,
  formatParams,
  isDevOrTestMode,
  isAdmin,
} from '@utils/general';
import { notifications } from '@utils/notifications/index';
import Dashboard from './pages/dashboard/page';
import StreamsPage from './pages/split/streams/page';
import CamerasPage from './pages/split/cameras/page';
import ZonesPage from './pages/zones/page';
import AppliancesPage from './pages/appliances/page';
import ReportsPage from './pages/reports/page';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import TTLCache from '@isaacs/ttlcache';
import ApiKeysPage from './pages/split/apikeys/page';
import { formatMessage, generatePartialFailMessage } from '@utils/formatter';
import { loadConfig, getConfig } from '@utils/config';
import {
  Picker as AnalyticsMarketplace,
  Home as StreamHealth,
} from 'stream-health';
import 'stream-health/dist/stream-health.css';
import { ConditionalWebSocketHandler } from '@components/WebSocketSetup/WebSocketSetup';

const logger = getLogger('src.pages.split.streams.bulkUpdate');

// initialize cache when app loads
export const cache = new TTLCache({
  max: 10000,
  ttl: 600000,
});
const log = getLogger('src.App');
let messenger = null;
const Router = isMicroapp() ? HashRouter : BrowserRouter;

function App() {
  const [userIsAdmin, setUserIsAdmin] = useState(isDevOrTestMode());
  const [isReady, setIsReady] = useState(!isMicroapp()); // non-microapp should be immediately ready

  useEffectOnce(() => {
    // The initialization logic for the microapp, including setting up the navigation and message handlers.
    const initialize = async () => {
      if (!isMicroapp()) {
        await notifications.init();
        return;
      }
      const adminStatus = await isAdmin();
      setUserIsAdmin(adminStatus);

      messenger = await generateMicroappMessenger();
      await notifications.init(messenger);

      messenger.registerNavigateHandler((data) => {
        if (!is.object(data)) {
          log.warn('The navigate message is poorly formated.');
          return;
        }
        log.info('data.path:', data.path);
        if (!is.string(data.path)) {
          log.warn('The naviagte message path is not a string.');
          return;
        }
        if (is.empty(data.path)) {
          log.warn('The navigate message path is empty.');
          return;
        }
        if (data.path.includes('blob:')) {
          log.info('Ignoring blob URL navigation');
          return;
        }
        if (window.location.href.endsWith(data.path)) {
          log.info(`Already at path ${data.path}`);
          return;
        }
        const path = data.path.startsWith('#') ? data.path : `#/${data.path}`;
        log.info('Redirecting to new path', path);

        // Must "replace" location to ensure the browser's history
        // does not capture duplicate navigation routes to the same location -
        // one for the inner app (Manager UI) and one for the shell (Gateway).
        window.location.replace(path + formatParams(data.queryParams));
      });

      messenger.sendLoad([
        createNavEntry('dashboard', {
          icon: 'fa-sharp fa-solid fa-chart-simple-horizontal',
        }),
        createNavEntry('cameras', {
          icon: 'fa-regular fa-video',
        }),
        createNavEntry('streams', {
          icon: 'fa-regular fa-waveform',
        }),
        createNavEntry('appliances', {
          icon: 'fa-solid fa-gear',
        }),
        createNavEntry('zones', {
          icon: 'fa-solid fa-map-location-dot',
        }),
        ...(adminStatus ? [
          createNavEntry('apikeys', {
            icon: 'fa-solid fa-key',
            displayName: 'API Keys',
          }),
        ] : []),
        createNavEntry('reports', {
          icon: 'fa-regular fa-file-chart-column',
        }),
        createNavEntry('analytics', {
          icon: 'fa-solid fa-chart-network',
          displayName: 'Analytics',
        }),
      ]);

      setIsReady(true);
    };

    initialize().catch((error) => log.error('Failed to initialize app:', error));
  });

  const handleBulkStreamUpdate = (success, trackingInfo) => {
    const bulkInfo = cache.get('bulkOperationCount');
    if (!bulkInfo) return;

    bulkInfo.completed++;
    if (success) {
      bulkInfo.successes++;
    } else {
      bulkInfo.failures++;
      if (trackingInfo.streamInfo) {
        bulkInfo.failedStreams.push(trackingInfo.streamInfo.name);
      }
    }

    if (bulkInfo.completed !== bulkInfo.total) {
      cache.set('bulkOperationCount', bulkInfo);
      return;
    }

    const successMessage =
      `Successfully updated ${bulkInfo.successes} stream${bulkInfo.successes > 1 ? 's' : ''}.`;
    const failMessage =
      `Failed to update ${bulkInfo.failures} stream${bulkInfo.failures > 1 ? 's' : ''}:
      <br/>${formatMessage(bulkInfo.failedStreams)}`;

    if (bulkInfo.successes > 0 && bulkInfo.failures > 0) {
      notifications.warn(generatePartialFailMessage(successMessage, failMessage));
    } else if (bulkInfo.successes > 0) {
      notifications.success(successMessage);
      logger.info(successMessage);
    } else if (bulkInfo.failures > 0) {
      notifications.error(failMessage);
      logger.error('Failed to update:', bulkInfo.failedStreams);
    }

    cache.delete('bulkOperationCount');
  };

  const handleSingleStreamUpdate = (success, trackingInfo) => {
    if (trackingInfo.suppressNotification) return;

    if (success) {
      notifications.success('Successfully updated the stream.');
    } else {
      notifications.error('Failed to update the stream.');
    }
  };

  /**
   * Event handler to show notification depending on message from the sub_channel
   * It will only show the notification if the tracking id (channel) exists in the cache
   * @param {*} info
   */
  const handleSubChannelMessage = (info) => {
    const details = info.detail;

    /**
     * Only want this to run on sub_channel messages
     * which have a message_id while mailbox messages do not
    */
    if (!details.message_id) return;

    const channel = details.channel;
    const trackingInfo = cache.get(channel);

    if (!trackingInfo) return;

    // Success codes will be less than 1100
    // Stream (SFS) error codes will be 1100 to 1199
    const success = details.data.response_code < 1100;

    if (trackingInfo.bulkUpdate) {
      handleBulkStreamUpdate(success, trackingInfo);
    } else {
      handleSingleStreamUpdate(success, trackingInfo);
    }

    // Remove the individual tracking ID
    cache.delete(channel);
  };

  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    if (isMicroapp()) {
      messageHandler.subscribe(handleSubChannelMessage);

      return () => {
        messageHandler.unsubscribe(handleSubChannelMessage);
      };
    }
  });

  const StreamHealthRoute = (
    <Route path='stream-health'
      element={<StreamHealth className={getConfig().STREAM_HEALTH_ENABLED !== 'true' ? 'disabled' : null}
        dataUrl={getConfig().STREAM_HEALTH_DATA_URL || '/api/stream-health'}
      />}
    />
  );

  if (!isReady) return null;
  return (
    <>
      <ConditionalWebSocketHandler />
      <Router>
        <Routes>
          {!isMicroapp() && <Route path='/' element={<Dashboard />} />}
          <Route path='/dashboard' element={<Dashboard />} />
          <Route path='/streams' element={<StreamsPage userIsAdmin={userIsAdmin} />} />
          <Route path='/cameras' element={<CamerasPage userIsAdmin={userIsAdmin} />} />
          <Route path='/zones' element={<ZonesPage userIsAdmin={userIsAdmin} />} />
          <Route path='/appliances' element={<AppliancesPage userIsAdmin={userIsAdmin} />} />
          {userIsAdmin && <Route path='/apikeys' element={<ApiKeysPage />} />}
          <Route path='/reports' element={<ReportsPage userIsAdmin={userIsAdmin} />} />
          {/* Analytics */}
          {
            <>
              <Route path='/analytics' element={<Outlet />}>
                <Route
                  index
                  element={
                    <AnalyticsMarketplace
                      routesEnabled={{
                        streamHealth: getConfig().STREAM_HEALTH_ENABLED === 'true',
                      }}
                    />
                  }
                />
                {StreamHealthRoute}
              </Route>
              {!isMicroapp() && StreamHealthRoute}
            </>
          }
        </Routes>
      </Router>
    </>
  );
}

async function initializeApp() {
  try {
    await loadConfig('config.json');

    // This logic exists to effectively handle navigation via
    // non-React Router mechanisms like anchor `<a>` tags.
    //
    // NOTE: This is critical for the "Stream Health" functionality
    //  to function properly while it is included in Manager UI.
    //  This function effectively handles in-App navigation via
    //  HTML anchor tags.
    if (isMicroapp()) {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a');

        if (link && link.attributes?.href?.value) {
          // Ignore blob URLs and download links
          const href = link.attributes.href.value;
          if (href.includes('blob:') || link.attributes.download) {
            return;  // Let the browser handle it
          }
          e.preventDefault();
          // Short-circuit the navigation logic if there is a single character
          // in the href value.
          if (link.attributes.href.value?.length <= 1) {
            // eslint-disable-next-line no-console
            console.log(link.attributes.href.value, link.attributes.href.value?.length);
            return;
          }
          // Custom navigation logic
          let newRelativeLocation = '';
          if (!link.attributes.href.value.startsWith('/')) {
            newRelativeLocation = window.location.hash + '/' + link.attributes.href.value;
          } else {
            newRelativeLocation = '#' + link.attributes.href.value;
          }

          // Pop off the `#/` from the beginning of the string
          const relativeLocation = newRelativeLocation.slice(2);
          // Handle query parameters if they exist
          const splitURI = relativeLocation.split('?');
          let paramsArray = [];
          if (splitURI.length) {
            const params = new URLSearchParams(splitURI[1]);
            for (const [key, value] of params) {
              paramsArray.push({ name: key, value });
            }
          }
          // use messenger already initialized in useEffectOnce to navigate to the new location via the shell
          if (messenger) {
            messenger.sendNavigate(splitURI[0], paramsArray);
          } else {
            log.error('Messenger not initialized, cannot navigate.');
          }
        }
      });
    }

    const root = createRoot(document.getElementById('root'));
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  } catch (error) {
    console.error('Failed to load configuration:', error);
  }
}

initializeApp();
